import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Tooltip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Divider,
  Paper,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Inventory as InventoryIcon,
  Forum as ChatIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';

import { Business, Theme, Inventory, Plate, Dish } from '../types';
import BusinessService from '../services/businessService';
import ThemeService from '../services/themeService';
import InventoryService from '../services/inventoryService';
import plateService from '../services/plateService';
import dishService from '../services/dishService';
import chatService from '../services/chatService';
import ThemeCard from '../components/ThemeCard';
import InventoryCard from '../components/InventoryCard';
import PlateCard from '../components/PlateCard';
import DishCard from '../components/DishCard';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../contexts/SearchContext';
import { calculateDistance, formatDistance } from '../utils/distanceUtils';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getAveragePrice, isWithinBudget } from '../utils/priceUtils';
import { ratingService } from '../services/ratingService';
import { RatingStats } from '../types/rating';
import FilterDialog, { FilterOptions } from '../components/FilterDialog';
import ActiveFiltersChips from '../components/ActiveFiltersChips';
import './ClientExploreChaavi.css';
import { APP_DISPLAY_NAME, pageTitle } from '../config/brand';

type LocationFilter = 'all' | 'nearby' | 'custom';
type BudgetFilter = 'all' | 'custom';
type SortOrder =
  | 'price-low'
  | 'price-high'
  | 'rating-high'
  | 'rating-low'
  | 'popular'
  | 'newest'
  | 'default';

const ClientExplore: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [plates, setPlates] = useState<Plate[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const [businessThemes, setBusinessThemes] = useState<{[key: string]: Theme[]}>({});
  const [businessInventory, setBusinessInventory] = useState<{[key: string]: Inventory[]}>({});
  const [businessPlates, setBusinessPlates] = useState<{[key: string]: Plate[]}>({});
  const [businessDishes, setBusinessDishes] = useState<{[key: string]: Dish[]}>({});
  const [chatLoading, setChatLoading] = useState<{[key: string]: boolean}>({});
  
  // Location-based filtering state
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all'); // Default to all
  const [appliedRadius, setAppliedRadius] = useState<number>(10); // The radius that's actually applied
  const [businessDistances, setBusinessDistances] = useState<{[key: string]: number}>({});
  const [locationRequested, setLocationRequested] = useState(false); // Track if location was requested
  
  // Budget-based filtering state
  const [budgetFilter, setBudgetFilter] = useState<BudgetFilter>('all');
  const [appliedMinBudget, setAppliedMinBudget] = useState<number>(0); // The min budget that's actually applied
  const [appliedMaxBudget, setAppliedMaxBudget] = useState<number>(100000); // The max budget that's actually applied
  const [sortOrder, setSortOrder] = useState<SortOrder>('default');
  
  // Dish type filter state
  const [dishTypeFilter, setDishTypeFilter] = useState<'all' | 'veg' | 'non-veg'>('all');
  // Plate type filter state
  const [plateTypeFilter, setPlateTypeFilter] = useState<'all' | 'veg' | 'non-veg'>('all');
  
  // Rating stats state
  const [themeRatingStats, setThemeRatingStats] = useState<{[key: string]: RatingStats}>({});
  const [inventoryRatingStats, setInventoryRatingStats] = useState<{[key: string]: RatingStats}>({});
  const [plateRatingStats, setPlateRatingStats] = useState<{[key: string]: RatingStats}>({});
  const [dishRatingStats, setDishRatingStats] = useState<{[key: string]: RatingStats}>({});
  
  const [buildPlateOpen, setBuildPlateOpen] = useState(false);
  const [buildPlateName, setBuildPlateName] = useState('');
  const [selectedDishKeys, setSelectedDishKeys] = useState<string[]>([]);
  const [selectedDishQuantities, setSelectedDishQuantities] = useState<Record<string, number>>({});
  const [customBuildDishes, setCustomBuildDishes] = useState<Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    dishType: 'veg' | 'non-veg';
  }>>([]);
  const [customBuildDishName, setCustomBuildDishName] = useState('');
  const [customBuildDishType, setCustomBuildDishType] = useState<'veg' | 'non-veg'>('veg');
  const [customBuildDishQuantity, setCustomBuildDishQuantity] = useState('1');
  const [customBuildDishError, setCustomBuildDishError] = useState<string | null>(null);
  const [comparisonTab, setComparisonTab] = useState(0);
  const [comparisonResults, setComparisonResults] = useState<Array<{
    businessId: string;
    businessName: string;
    matchedDishes: number;
    totalPrice: number;
    avgRating: number;
    matchedDishNames: string[];
  }>>([]);
  
  // Filter Dialog state
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [showCategorySelection, setShowCategorySelection] = useState(true);
  const { 
    searchQuery, 
    setSearchSuggestions,
  } = useSearch();
  
  // Listen for filter dialog open event from header
  useEffect(() => {
    const handleOpenFilterDialog = () => {
      setFilterDialogOpen(true);
    };
    
    window.addEventListener('openFilterDialog', handleOpenFilterDialog);
    
    return () => {
      window.removeEventListener('openFilterDialog', handleOpenFilterDialog);
    };
  }, []);
  
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    eventType: 'all',
    category: selectedCategory || 'all',
    location: locationFilter || 'all',
    radius: appliedRadius || 10,
    budget: budgetFilter || 'all',
    minBudget: appliedMinBudget || 0,
    maxBudget: appliedMaxBudget || 100000,
    sortBy: sortOrder || 'default',
    minRating: 0,
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const isVendorViewer = user?.userType === 'VENDOR';

  const [welcomeBannerVisible, setWelcomeBannerVisible] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined' || !user) return;
    const key = user.userType === 'VENDOR' ? 'vendorExploreWelcomeDismissed' : 'welcomeBannerDismissed';
    setWelcomeBannerVisible(localStorage.getItem(key) !== 'true');
  }, [user]);

  const dismissWelcomeBanner = useCallback(() => {
    setWelcomeBannerVisible(false);
    const key = user?.userType === 'VENDOR' ? 'vendorExploreWelcomeDismissed' : 'welcomeBannerDismissed';
    localStorage.setItem(key, 'true');
  }, [user?.userType]);

  const chaaviCounts = useMemo(() => {
    const catLower = (b: Business) => (b.businessCategory || '').toLowerCase();
    const isCatering = (b: Business) => {
      const c = catLower(b);
      return (
        (c.includes('catering') ||
          c.includes('caters') ||
          c.includes('food') ||
          c.includes('restaurant') ||
          c.includes('cafe')) &&
        !c.includes('tent') &&
        !c.includes('hotel')
      );
    };
    const isHotel = (b: Business) => {
      const c = catLower(b);
      return (
        c.includes('hotel') ||
        c.includes('farmhouse') ||
        c.includes('farm house') ||
        c.includes('venue') ||
        c.includes('resort')
      );
    };
    return {
      themeOptions: themes.length,
      cateringVendors: businesses.filter(isCatering).length,
      venues: businesses.filter(isHotel).length,
      decorItems: inventory.length,
    };
  }, [businesses, themes, inventory]);

  const vendorThemesTopRated = useCallback(
    (themeList: Theme[]) => {
      if (themeList.length === 0) return false;
      const avgs = themeList
        .map((t) => themeRatingStats[t.themeId]?.averageRating)
        .filter((x): x is number => typeof x === 'number' && x > 0);
      if (avgs.length === 0) return false;
      const mean = avgs.reduce((a, b) => a + b, 0) / avgs.length;
      return mean >= 4.5;
    },
    [themeRatingStats]
  );

  const handleChaaviCategory = (cat: 'tent' | 'caters' | 'hotel') => {
    setShowCategorySelection(false);
    setSelectedCategory(cat);
    setAppliedFilters(prev => ({ ...prev, category: cat }));
  };

  useEffect(() => {
    const prev = document.title;
    document.title = pageTitle('Explore');
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    fetchData();
    // Automatically request location once on page load
    if (!locationRequested && navigator.geolocation) {
      getCurrentLocation(false); // false = automatic request
      setLocationRequested(true);
    }
    // Intentionally omit fetchData/getCurrentLocation: mount + locationRequested gate only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationRequested]);

  useEffect(() => {
    if (selectedCategory) {
      filterBusinessesByCategory(selectedCategory);
    }
    // filterBusinessesByCategory recreated each render; category + businesses drive this pass
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, businesses]);

  // Calculate distances from user location to all businesses
  const calculateDistances = useCallback(() => {
    if (!userLocation) return;

    const distances: {[key: string]: number} = {};
    businesses.forEach(business => {
      if (business.latitude && business.longitude) {
        const distance = calculateDistance(
          userLocation.lat,
          userLocation.lon,
          business.latitude,
          business.longitude
        );
        distances[business.businessId] = distance;
      }
    });
    setBusinessDistances(distances);
  }, [userLocation, businesses]);

  // Load rating stats for all items
  const loadRatingStats = useCallback(async () => {
    if (themes.length === 0 && inventory.length === 0 && plates.length === 0 && dishes.length === 0) return;
    
    try {
      const themeStatsPromises = themes.map(theme =>
        ratingService.getRatingStats(theme.themeId, 'THEME').catch(() => null)
      );
      const inventoryStatsPromises = inventory.map(item =>
        ratingService.getRatingStats(item.inventoryId, 'INVENTORY').catch(() => null)
      );
      const plateStatsPromises = plates.map(plate =>
        ratingService.getRatingStats(plate.plateId, 'PLATE').catch(() => null)
      );
      const dishStatsPromises = dishes.map(dish =>
        ratingService.getRatingStats(dish.dishId, 'DISH').catch(() => null)
      );

      const [themeStatsResults, inventoryStatsResults, plateStatsResults, dishStatsResults] = await Promise.all([
        Promise.all(themeStatsPromises),
        Promise.all(inventoryStatsPromises),
        Promise.all(plateStatsPromises),
        Promise.all(dishStatsPromises)
      ]);

      // Build rating stats maps
      const themeStatsMap: {[key: string]: RatingStats} = {};
      themes.forEach((theme, index) => {
        if (themeStatsResults[index]) {
          themeStatsMap[theme.themeId] = themeStatsResults[index]!;
        }
      });

      const inventoryStatsMap: {[key: string]: RatingStats} = {};
      inventory.forEach((item, index) => {
        if (inventoryStatsResults[index]) {
          inventoryStatsMap[item.inventoryId] = inventoryStatsResults[index]!;
        }
      });

      const plateStatsMap: {[key: string]: RatingStats} = {};
      plates.forEach((plate, index) => {
        if (plateStatsResults[index]) {
          plateStatsMap[plate.plateId] = plateStatsResults[index]!;
        }
      });

      const dishStatsMap: {[key: string]: RatingStats} = {};
      dishes.forEach((dish, index) => {
        if (dishStatsResults[index]) {
          dishStatsMap[dish.dishId] = dishStatsResults[index]!;
        }
      });

      setThemeRatingStats(themeStatsMap);
      setInventoryRatingStats(inventoryStatsMap);
      setPlateRatingStats(plateStatsMap);
      setDishRatingStats(dishStatsMap);
    } catch (err) {
      console.error('Error loading rating stats:', err);
    } finally {
    }
  }, [themes, inventory, plates, dishes]);

  useEffect(() => {
    if (userLocation && locationFilter !== 'all') {
      calculateDistances();
    }
  }, [userLocation, locationFilter, appliedRadius, businesses, calculateDistances]);

  useEffect(() => {
    loadRatingStats();
  }, [themes, inventory, plates, dishes, loadRatingStats]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [businessesData, themesData, inventoryData, platesData, dishesData] = await Promise.all([
        BusinessService.getAllBusinesses(),
        ThemeService.getAllThemes(),
        InventoryService.getAllInventory(),
        plateService.getAllPlates(),
        dishService.getAllDishes()
      ]);
      
      setBusinesses(businessesData);
      setThemes(themesData);
      setInventory(inventoryData);
      setPlates(platesData);
      setDishes(dishesData);
      
      // Apply category filter on load
      filterBusinessesByCategory(selectedCategory || 'all', businessesData);
      
      // Group themes by business
      const themesByBusiness: {[key: string]: Theme[]} = {};
      themesData.forEach(theme => {
        if (!themesByBusiness[theme.businessId]) {
          themesByBusiness[theme.businessId] = [];
        }
        themesByBusiness[theme.businessId].push(theme);
      });
      setBusinessThemes(themesByBusiness);
      
      // Group inventory by business
      const inventoryByBusiness: {[key: string]: Inventory[]} = {};
      inventoryData.forEach(item => {
        if (!inventoryByBusiness[item.businessId]) {
          inventoryByBusiness[item.businessId] = [];
        }
        inventoryByBusiness[item.businessId].push(item);
      });
      setBusinessInventory(inventoryByBusiness);
      
      // Group plates by business
      const platesByBusiness: {[key: string]: Plate[]} = {};
      platesData.forEach(plate => {
        if (!platesByBusiness[plate.businessId]) {
          platesByBusiness[plate.businessId] = [];
        }
        platesByBusiness[plate.businessId].push(plate);
      });
      setBusinessPlates(platesByBusiness);
      
      // Group dishes by business
      const dishesByBusiness: {[key: string]: Dish[]} = {};
      dishesData.forEach(dish => {
        if (!dishesByBusiness[dish.businessId]) {
          dishesByBusiness[dish.businessId] = [];
        }
        dishesByBusiness[dish.businessId].push(dish);
      });
      setBusinessDishes(dishesByBusiness);
      
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const filterBusinessesByCategory = (category: string, sourceBusinesses?: Business[]) => {
    const list = sourceBusinesses || businesses;
    if (category === 'all') {
      setFilteredBusinesses(list);
      return;
    }
    
    const filtered = list.filter(business => {
      const categoryLower = business.businessCategory.toLowerCase();
      
      if (category === 'tent' || category === 'decoration') {
        // Only tent/decoration businesses - exclude catering, hotel, farmhouse
        return (categoryLower.includes('tent') || 
               categoryLower.includes('event') || 
               categoryLower.includes('wedding') ||
               categoryLower.includes('party') ||
               categoryLower.includes('decoration')) &&
               !categoryLower.includes('catering') &&
               !categoryLower.includes('caters') &&
               !categoryLower.includes('food') &&
               !categoryLower.includes('restaurant') &&
               !categoryLower.includes('hotel') &&
               !categoryLower.includes('farmhouse') &&
               !categoryLower.includes('farm house');
      } else if (category === 'catering' || category === 'caters') {
        // Only catering businesses - exclude tent/decoration, hotel, farmhouse
        return (categoryLower.includes('catering') || 
               categoryLower.includes('caters') ||
               categoryLower.includes('food') || 
               categoryLower.includes('restaurant') ||
               categoryLower.includes('cafe')) &&
               !categoryLower.includes('tent') &&
               !categoryLower.includes('decoration') &&
               !categoryLower.includes('event') &&
               !categoryLower.includes('wedding') &&
               !categoryLower.includes('party') &&
               !categoryLower.includes('hotel') &&
               !categoryLower.includes('farmhouse') &&
               !categoryLower.includes('farm house');
      } else if (category === 'hotel') {
        // Only hotel and farmhouse businesses
        return (categoryLower.includes('hotel') || 
               categoryLower.includes('farmhouse') ||
               categoryLower.includes('farm house') ||
               categoryLower.includes('venue') ||
               categoryLower.includes('resort')) &&
               !categoryLower.includes('catering') &&
               !categoryLower.includes('caters') &&
               !categoryLower.includes('food') &&
               !categoryLower.includes('restaurant') &&
               !categoryLower.includes('tent') &&
               !categoryLower.includes('decoration');
      }

      const blob = `${business.businessName} ${business.businessCategory} ${business.businessDescription || ''}`.toLowerCase();
      const hasKeyword = (words: string[]) => words.some(w => blob.includes(w));
      if (category === 'photography') {
        return hasKeyword(['photo', 'photograph', 'photography', 'videograph', 'videography', 'cinematography', 'camera']);
      }
      if (category === 'music') {
        return hasKeyword(['music', 'dj', 'disc jockey', 'band', 'orchestra', 'sound system', 'audio']);
      }
      if (category === 'makeup') {
        return hasKeyword(['makeup', 'make-up', 'beautician', 'bridal makeup', 'hair stylist', 'mua']);
      }
      if (category === 'transport') {
        return hasKeyword(['transport', 'logistics', 'car rental', 'bus', 'vehicle', 'shuttle', 'travel', 'fleet']);
      }
      return false;
    });
    
    setFilteredBusinesses(filtered);
  };

  // Get user's current location
  const getCurrentLocation = (isManual: boolean = true) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by your browser');
      // If geolocation not supported, keep location filter as 'all'
      setLocationFilter('all');
      if (isManual) {
        alert('Geolocation is not supported by your browser');
      }
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lon: longitude });
        // Location is obtained but filter remains as user selected (default: 'all')
      },
      (error) => {
        console.error('Error getting location:', error);
        // If location access denied or failed, fall back to 'all' filter
        setLocationFilter('all');
        // Only show alert on manual request, not on automatic request
        if (isManual) {
          alert('Unable to get your location. Please enable location permissions.');
        }
      }
    );
  };

  // Sort businesses by distance
  const sortBusinessesByDistance = (businessList: Business[]): Business[] => {
    if (locationFilter === 'all' || !userLocation) {
      return businessList;
    }

    const radius = appliedRadius;
    
    return businessList
      .filter(business => {
        if (!business.latitude || !business.longitude) return false;
        const distance = businessDistances[business.businessId];
        return distance !== undefined && distance <= radius;
      })
      .sort((a, b) => {
        const distA = businessDistances[a.businessId] || Infinity;
        const distB = businessDistances[b.businessId] || Infinity;
        return distA - distB;
      });
  };

  // Sort themes/plates by business distance
  const sortItemsByDistance = <T extends { businessId: string }>(
    items: T[]
  ): T[] => {
    if (locationFilter === 'all' || !userLocation) {
      return items;
    }

    const radius = appliedRadius;

    return items
      .filter(item => {
        const business = businesses.find(b => b.businessId === item.businessId);
        if (!business || !business.latitude || !business.longitude) return false;
        const distance = businessDistances[business.businessId];
        return distance !== undefined && distance <= radius;
      })
      .sort((a, b) => {
        const distA = businessDistances[a.businessId] || Infinity;
        const distB = businessDistances[b.businessId] || Infinity;
        return distA - distB;
      });
  };

  // Apply filters from dialog
  const applyFilters = (filters: FilterOptions) => {
    setSelectedCategory(filters.category);
    setLocationFilter(filters.location);
    setAppliedRadius(filters.radius);
    setBudgetFilter(filters.budget);
    setAppliedMinBudget(filters.minBudget);
    setAppliedMaxBudget(filters.maxBudget);
    setSortOrder(filters.sortBy);
    
    // Filter businesses by category
    filterBusinessesByCategory(filters.category);
    
    // If location filter is not 'all', ensure we have user location
    if (filters.location !== 'all' && !userLocation) {
      getCurrentLocation(true);
    }
  };

  // Get default value for a filter key
  const getDefaultValue = (key: keyof FilterOptions): any => {
    const defaults: FilterOptions = {
      eventType: 'all',
      category: 'all',
      location: 'all',
      radius: 10,
      budget: 'all',
      minBudget: 0,
      maxBudget: 100000,
      sortBy: 'default',
      minRating: 0,
    };
    return defaults[key];
  };

  // Get budget range based on filter
  const getBudgetRange = (): { min: number; max: number } => {
    if (budgetFilter === 'custom') {
      return { min: appliedMinBudget, max: appliedMaxBudget };
    }
    // For 'all', return full range
    return { min: 0, max: Infinity };
  };

  // Get average rating for a theme
  const getThemeRating = (themeId: string): number => {
    const stats = themeRatingStats[themeId];
    return stats?.averageRating || 0;
  };

  // Get average rating for inventory
  const getInventoryRating = (inventoryId: string): number => {
    const stats = inventoryRatingStats[inventoryId];
    return stats?.averageRating || 0;
  };

  // Get average rating for plate
  const getPlateRating = (plateId: string): number => {
    const stats = plateRatingStats[plateId];
    return stats?.averageRating || 0;
  };

  // Get average rating for dish
  const getDishRating = (dishId: string): number => {
    const stats = dishRatingStats[dishId];
    return stats?.averageRating || 0;
  };
  
  type DishOption = { key: string; label: string; vendorsCount: number; dishIds: string[] };
  
  const dishOptions = React.useMemo<DishOption[]>(() => {
    const optionMap = new Map<string, { key: string; label: string; vendors: Set<string>; dishIds: string[] }>();
    dishes.forEach(dish => {
      const label = dish.dishName?.trim();
      if (!label) return;
      const key = label.toLowerCase();
      const entry = optionMap.get(key) || { key, label, vendors: new Set<string>(), dishIds: [] };
      entry.vendors.add(dish.businessId);
      if (!entry.dishIds.includes(dish.dishId)) {
        entry.dishIds.push(dish.dishId);
      }
      optionMap.set(key, entry);
    });
    return Array.from(optionMap.values())
      .map(entry => ({
        key: entry.key,
        label: entry.label,
        vendorsCount: entry.vendors.size,
        dishIds: entry.dishIds,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dishes]);
  
  const dishOptionMap = React.useMemo(() => {
    const map = new Map<string, DishOption>();
    dishOptions.forEach(option => map.set(option.key, option));
    return map;
  }, [dishOptions]);

  const handleAddCustomBuildDish = () => {
    const name = customBuildDishName.trim();
    const quantity = parseInt(customBuildDishQuantity) || 1;

    if (!name) {
      setCustomBuildDishError('Please enter a dish name.');
      return;
    }

    setCustomBuildDishes(prev => ([
      ...prev,
      {
        id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        name,
        price: 0,
        quantity: Math.max(1, quantity),
        dishType: customBuildDishType,
      }
    ]));
    setCustomBuildDishName('');
    setCustomBuildDishType('veg');
    setCustomBuildDishQuantity('1');
    setCustomBuildDishError(null);
  };
  
  const handleBuildPlateCompare = () => {
    const selected = selectedDishKeys
      .map(key => dishOptionMap.get(key))
      .filter(Boolean) as Array<{ key: string; label: string; vendorsCount: number; dishIds: string[] }>;
    if (selected.length === 0) {
      setComparisonResults([]);
      return;
    }
    
    const cateringBusinesses = businesses.filter(business => {
      const category = business.businessCategory?.toLowerCase() || '';
      return category.includes('catering') ||
        category.includes('caters') ||
        category.includes('food') ||
        category.includes('restaurant') ||
        category.includes('cafe');
    });
    
    const results = cateringBusinesses.map(business => {
      const vendorDishes = businessDishes[business.businessId] || [];
      let matchedDishes = 0;
      let totalPrice = 0;
      const matchedRatings: number[] = [];
      const matchedDishNames: string[] = [];
      
      selected.forEach(option => {
        const vendorMatches = vendorDishes.filter(dish => dish.dishName?.trim().toLowerCase() === option.key);
        if (vendorMatches.length === 0) return;
        
        matchedDishes += 1;
        matchedDishNames.push(option.label);
        
        const minPrice = Math.min(...vendorMatches.map(dish => dish.price || 0));
        const quantity = Math.max(1, selectedDishQuantities[option.key] || 1);
        totalPrice += minPrice * quantity;
        
        vendorMatches.forEach(match => {
          const rating = getDishRating(match.dishId);
          if (rating > 0) {
            matchedRatings.push(rating);
          }
        });
      });

      if (customBuildDishes.length > 0) {
        const customTotal = customBuildDishes.reduce(
          (sum, dish) => sum + dish.price * dish.quantity,
          0
        );
        totalPrice += customTotal;
      }
      
      const avgRating = matchedRatings.length > 0
        ? matchedRatings.reduce((sum, rating) => sum + rating, 0) / matchedRatings.length
        : 0;
      
      return {
        businessId: business.businessId,
        businessName: business.businessName,
        matchedDishes,
        totalPrice,
        avgRating,
        matchedDishNames,
      };
    }).filter(result => result.matchedDishes > 0);
    
    setComparisonResults(results);
    setComparisonTab(0);
  };
  
  const businessMap = React.useMemo(() => {
    return new Map(businesses.map(business => [business.businessId, business]));
  }, [businesses]);
  
  const comparisonBuckets = React.useMemo(() => {
    const bestMatch = [...comparisonResults].sort((a, b) => {
      if (b.matchedDishes !== a.matchedDishes) return b.matchedDishes - a.matchedDishes;
      if (a.totalPrice !== b.totalPrice) return a.totalPrice - b.totalPrice;
      return b.avgRating - a.avgRating;
    });
    const cheapest = [...comparisonResults].sort((a, b) => a.totalPrice - b.totalPrice);
    const topRated = [...comparisonResults].sort((a, b) => b.avgRating - a.avgRating);
    return { bestMatch, cheapest, topRated };
  }, [comparisonResults]);

  // Generate search suggestions based on query
  const getSearchSuggestions = useCallback(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];
    
    const query = searchQuery.toLowerCase().trim();
    const suggestions: Array<{ type: 'theme' | 'inventory' | 'plate' | 'dish'; name: string; id: string }> = [];
    
    // Search themes
    themes.forEach(theme => {
      if (theme.themeName?.toLowerCase().includes(query)) {
        suggestions.push({ type: 'theme', name: theme.themeName, id: theme.themeId });
      }
    });
    
    // Search inventory
    inventory.forEach(item => {
      if (item.inventoryName?.toLowerCase().includes(query)) {
        suggestions.push({ type: 'inventory', name: item.inventoryName, id: item.inventoryId });
      }
    });
    
    // Search plates
    plates.forEach(plate => {
      if (plate.dishName?.toLowerCase().includes(query)) {
        suggestions.push({ type: 'plate', name: plate.dishName, id: plate.plateId });
      }
    });
    
    // Search dishes
    dishes.forEach(dish => {
      if (dish.dishName?.toLowerCase().includes(query)) {
        suggestions.push({ type: 'dish', name: dish.dishName, id: dish.dishId });
      }
    });
    
    // Remove duplicates and limit to 8 suggestions
    const uniqueSuggestions = suggestions.filter((s, index, self) => 
      index === self.findIndex(t => t.name === s.name && t.type === s.type)
    );
    
    return uniqueSuggestions.slice(0, 8);
  }, [searchQuery, themes, inventory, plates, dishes]);
  
  const searchSuggestions = getSearchSuggestions();
  
  // Update search suggestions in context
  useEffect(() => {
    setSearchSuggestions(searchSuggestions);
  }, [searchSuggestions, setSearchSuggestions]);
  
  // Listen for scroll to item event from Navigation
  useEffect(() => {
    const handleScrollToItem = (event: CustomEvent) => {
      const { type, id } = event.detail;
      setTimeout(() => {
        const elementId = `${type}-${id}`;
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Highlight the element briefly
          element.style.transition = 'box-shadow 0.3s ease';
          element.style.boxShadow = '0 0 20px rgba(102, 126, 234, 0.6)';
          setTimeout(() => {
            element.style.boxShadow = '';
          }, 2000);
        }
      }, 100);
    };
    
    window.addEventListener('scrollToSearchItem', handleScrollToItem as EventListener);
    return () => {
      window.removeEventListener('scrollToSearchItem', handleScrollToItem as EventListener);
    };
  }, []);

  // Filter and sort themes by budget and rating
  const filterAndSortThemes = (themeList: Theme[]): Theme[] => {
    const budgetRange = getBudgetRange();
    
    let filtered = themeList.filter(theme => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = theme.themeName?.toLowerCase().includes(query);
        const matchesDescription = theme.themeDescription?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }
      if (!isWithinBudget(undefined, theme.priceRange, budgetRange.min, budgetRange.max)) return false;
      if (appliedFilters.minRating > 0 && getThemeRating(theme.themeId) < appliedFilters.minRating) return false;
      return true;
    });

    if (sortOrder === 'price-low') {
      filtered.sort((a, b) => {
        const avgA = getAveragePrice(a.priceRange);
        const avgB = getAveragePrice(b.priceRange);
        return avgA - avgB;
      });
    } else if (sortOrder === 'price-high') {
      filtered.sort((a, b) => {
        const avgA = getAveragePrice(a.priceRange);
        const avgB = getAveragePrice(b.priceRange);
        return avgB - avgA;
      });
    } else if (sortOrder === 'rating-high') {
      filtered.sort((a, b) => {
        const ratingA = getThemeRating(a.themeId);
        const ratingB = getThemeRating(b.themeId);
        return ratingB - ratingA; // High to low
      });
    } else if (sortOrder === 'rating-low') {
      filtered.sort((a, b) => {
        const ratingA = getThemeRating(a.themeId);
        const ratingB = getThemeRating(b.themeId);
        return ratingA - ratingB; // Low to high
      });
    } else if (sortOrder === 'popular') {
      filtered.sort((a, b) => {
        const nA = themeRatingStats[a.themeId]?.totalRatings ?? 0;
        const nB = themeRatingStats[b.themeId]?.totalRatings ?? 0;
        if (nB !== nA) return nB - nA;
        return getThemeRating(b.themeId) - getThemeRating(a.themeId);
      });
    } else if (sortOrder === 'newest') {
      filtered.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }

    return filtered;
  };

  // Filter and sort inventory by budget and rating
  const filterAndSortInventory = (inventoryList: Inventory[]): Inventory[] => {
    const budgetRange = getBudgetRange();
    
    let filtered = inventoryList.filter(item => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = item.inventoryName?.toLowerCase().includes(query);
        const matchesDescription = item.inventoryDescription?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }
      if (!isWithinBudget(item.price, undefined, budgetRange.min, budgetRange.max)) return false;
      if (appliedFilters.minRating > 0 && getInventoryRating(item.inventoryId) < appliedFilters.minRating) return false;
      return true;
    });

    if (sortOrder === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOrder === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortOrder === 'rating-high') {
      filtered.sort((a, b) => {
        const ratingA = getInventoryRating(a.inventoryId);
        const ratingB = getInventoryRating(b.inventoryId);
        return ratingB - ratingA; // High to low
      });
    } else if (sortOrder === 'rating-low') {
      filtered.sort((a, b) => {
        const ratingA = getInventoryRating(a.inventoryId);
        const ratingB = getInventoryRating(b.inventoryId);
        return ratingA - ratingB; // Low to high
      });
    } else if (sortOrder === 'popular') {
      filtered.sort((a, b) => {
        const nA = inventoryRatingStats[a.inventoryId]?.totalRatings ?? 0;
        const nB = inventoryRatingStats[b.inventoryId]?.totalRatings ?? 0;
        if (nB !== nA) return nB - nA;
        return getInventoryRating(b.inventoryId) - getInventoryRating(a.inventoryId);
      });
    } else if (sortOrder === 'newest') {
      filtered.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }

    return filtered;
  };

  // Filter and sort plates by budget and rating
  const filterAndSortPlates = (plateList: Plate[]): Plate[] => {
    const budgetRange = getBudgetRange();
    
    let filtered = plateList.filter(plate => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = plate.dishName?.toLowerCase().includes(query);
        const matchesDescription = plate.dishDescription?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }
      // Plate type filter
      if (plateTypeFilter !== 'all') {
        // Check if plate has a dishType
        if (!plate.dishType) {
          return false;
        }
        
        // Normalize both values for comparison: lowercase, trim, remove hyphens/underscores/spaces
        const normalize = (val: string): string => {
          return String(val).toLowerCase().trim().replace(/[-_\s]/g, '');
        };
        
        const normalizedPlateType = normalize(plate.dishType);
        const normalizedFilter = normalize(plateTypeFilter);
        
        // Compare normalized values
        // 'veg' stays 'veg', 'non-veg' becomes 'nonveg'
        if (normalizedPlateType !== normalizedFilter) {
          return false;
        }
      }
      if (!isWithinBudget(plate.price, undefined, budgetRange.min, budgetRange.max)) return false;
      if (appliedFilters.minRating > 0 && getPlateRating(plate.plateId) < appliedFilters.minRating) return false;
      return true;
    });

    if (sortOrder === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOrder === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortOrder === 'rating-high') {
      filtered.sort((a, b) => {
        const ratingA = getPlateRating(a.plateId);
        const ratingB = getPlateRating(b.plateId);
        return ratingB - ratingA; // High to low
      });
    } else if (sortOrder === 'rating-low') {
      filtered.sort((a, b) => {
        const ratingA = getPlateRating(a.plateId);
        const ratingB = getPlateRating(b.plateId);
        return ratingA - ratingB; // Low to high
      });
    } else if (sortOrder === 'popular') {
      filtered.sort((a, b) => {
        const nA = plateRatingStats[a.plateId]?.totalRatings ?? 0;
        const nB = plateRatingStats[b.plateId]?.totalRatings ?? 0;
        if (nB !== nA) return nB - nA;
        return getPlateRating(b.plateId) - getPlateRating(a.plateId);
      });
    } else if (sortOrder === 'newest') {
      filtered.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }

    return filtered;
  };

  const filterAndSortDishes = (dishList: Dish[]): Dish[] => {
    const budgetRange = getBudgetRange();
    
    // Debug: Log first few dishes to see their dishType values
    if (dishList.length > 0 && dishTypeFilter !== 'all') {
      console.log('🔍 Filtering dishes:', {
        filter: dishTypeFilter,
        totalDishes: dishList.length,
        sampleDishes: dishList.slice(0, 3).map(d => ({
          name: d.dishName,
          dishType: d.dishType,
          hasDishType: !!d.dishType
        }))
      });
    }
    
    let filtered = dishList.filter(dish => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = dish.dishName?.toLowerCase().includes(query);
        const matchesDescription = dish.dishDescription?.toLowerCase().includes(query);
        if (!matchesName && !matchesDescription) return false;
      }
      // Dish type filter
      if (dishTypeFilter !== 'all') {
        // Get dishType from dish, default to 'veg' if null/undefined/empty (matching backend)
        let dishType = dish.dishType;
        if (!dishType || dishType.trim() === '') {
          dishType = 'veg';
        }
        
        // Normalize both for comparison: lowercase and remove hyphens/underscores/spaces
        const normalize = (val: string): string => {
          return val.toLowerCase().trim().replace(/[-_\s]/g, '');
        };
        
        const dishTypeNormalized = normalize(dishType);
        const filterNormalized = normalize(dishTypeFilter);
        
        // Debug: Log mismatch for first dish
        if (dishList.length > 0 && dishList.indexOf(dish) === 0) {
          console.log('🔍 Dish filter comparison:', {
            originalDishType: dish.dishType,
            normalizedDishType: dishTypeNormalized,
            filter: dishTypeFilter,
            normalizedFilter: filterNormalized,
            matches: dishTypeNormalized === filterNormalized
          });
        }
        
        // Only include dishes where normalized types match
        if (dishTypeNormalized !== filterNormalized) {
          return false;
        }
      }
      if (!isWithinBudget(dish.price, undefined, budgetRange.min, budgetRange.max)) return false;
      if (appliedFilters.minRating > 0 && getDishRating(dish.dishId) < appliedFilters.minRating) return false;
      return true;
    });

    if (sortOrder === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOrder === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortOrder === 'rating-high') {
      filtered.sort((a, b) => {
        const ratingA = getDishRating(a.dishId);
        const ratingB = getDishRating(b.dishId);
        return ratingB - ratingA; // High to low
      });
    } else if (sortOrder === 'rating-low') {
      filtered.sort((a, b) => {
        const ratingA = getDishRating(a.dishId);
        const ratingB = getDishRating(b.dishId);
        return ratingA - ratingB; // Low to high
      });
    } else if (sortOrder === 'popular') {
      filtered.sort((a, b) => {
        const nA = dishRatingStats[a.dishId]?.totalRatings ?? 0;
        const nB = dishRatingStats[b.dishId]?.totalRatings ?? 0;
        if (nB !== nA) return nB - nA;
        return getDishRating(b.dishId) - getDishRating(a.dishId);
      });
    } else if (sortOrder === 'newest') {
      filtered.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
    }

    return filtered;
  };

  // Buy Now handlers
  // Note: The card components handle adding to cart and opening cart.
  // These handlers can be used for additional logic like notifications or analytics.
  const handleThemeBuyNow = (theme: Theme, business: Business) => {
    // Item is already added to cart by the card component
    // You can add additional logic here like showing a success message
  };

  const handleInventoryBuyNow = (inventory: Inventory, business: Business) => {
    // Item is already added to cart by the card component
    // You can add additional logic here like showing a success message
  };

  const handlePlateBuyNow = (plate: Plate, business: Business) => {
    // Item is already added to cart by the card component
    // You can add additional logic here like showing a success message
  };

  const handleDishBuyNow = (dish: Dish, business: Business) => {
    // Item is already added to cart by the card component
    // You can add additional logic here like showing a success message
  };

  const handleStartChat = async (business: Business) => {
    if (!user) {
      alert('Please log in to start a chat');
      return;
    }

    const businessId = business.businessId;
    setChatLoading(prev => ({ ...prev, [businessId]: true }));

    try {
      // Get vendor phone from business data
      const vendorPhone = business.phoneNumber;
      
      // Create or get chat
      await chatService.createOrGetChat(
        user.phoneNumber,
        vendorPhone,
        business.businessId,
        business.businessName
      );

      // Navigate to chat page
      navigate('/client-chat');
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat. Please try again.');
    } finally {
      setChatLoading(prev => ({ ...prev, [businessId]: false }));
    }
  };

  const vendorCardEmoji = (b: Business) => {
    const c = (b.businessCategory || '').toLowerCase();
    if (c.includes('catering') || c.includes('caters') || c.includes('food') || c.includes('restaurant')) {
      return '🍛';
    }
    if (c.includes('hotel') || c.includes('venue') || c.includes('farm')) {
      return '🏛️';
    }
    if (c.includes('tent') || c.includes('decor') || c.includes('wedding') || c.includes('event')) {
      return '💐';
    }
    return '🎉';
  };

  if (loading) {
    return (
      <div className="cex-chaavi-root">
        <div className="cex-loading-wrap">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cex-chaavi-root">
        <div className="cex-error-banner" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const firstName = user?.firstName || 'there';

  return (
    <div className="cex-chaavi-root">
      <div className="cex-spark cex-spark-1" aria-hidden />
      <div className="cex-spark cex-spark-2" aria-hidden />
      <div className="cex-spark cex-spark-3" aria-hidden />

      <main className="cex-main">
        {welcomeBannerVisible && (
          <div className="cex-welcome-banner">
            <button
              type="button"
              className="cex-wb-close"
              onClick={dismissWelcomeBanner}
              aria-label="Dismiss welcome banner"
            >
              ✕
            </button>
            <div className="cex-wb-top">
              <div className="cex-wb-left">
                <div className="cex-wb-eyebrow">
                  {isVendorViewer ? 'Marketplace preview' : 'Getting started'}
                </div>
                <div className="cex-wb-title">
                  Welcome back, <em>{firstName}!</em>
                </div>
                <div className="cex-wb-sub">
                  {isVendorViewer
                    ? `See ${APP_DISPLAY_NAME} through your clients’ eyes—browse peers, listings, and inspiration.`
                    : "New to our platform? Here's how it works:"}
                </div>
                <div className="cex-wb-steps">
                  {isVendorViewer ? (
                    <>
                      <div className="cex-wb-step">
                        <div className="cex-wb-step-num">1</div>
                        <span>🔭</span>
                        <span>Browse categories</span>
                      </div>
                      <div className="cex-wb-step">
                        <div className="cex-wb-step-num">2</div>
                        <span>📈</span>
                        <span>Compare listings</span>
                      </div>
                      <div className="cex-wb-step">
                        <div className="cex-wb-step-num">3</div>
                        <span>💬</span>
                        <span>Message vendors</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="cex-wb-step">
                        <div className="cex-wb-step-num">1</div>
                        <span>🏆</span>
                        <span>Browse Services</span>
                      </div>
                      <div className="cex-wb-step">
                        <div className="cex-wb-step-num">2</div>
                        <span>🛒</span>
                        <span>Add to Cart</span>
                      </div>
                      <div className="cex-wb-step">
                        <div className="cex-wb-step-num">3</div>
                        <span>✅</span>
                        <span>Place Order</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="cex-wb-btns">
                  <button type="button" className="cex-wb-btn-primary" onClick={dismissWelcomeBanner}>
                    Got it, thanks!
                  </button>
                  <button
                    type="button"
                    className="cex-wb-btn-secondary"
                    onClick={() => {
                      document.getElementById('chaavi-categories')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Show me more tips
                  </button>
                </div>
              </div>
              <div className="cex-wb-deco">
                <div className="cex-wb-deco-icon">🎉</div>
                <div className="cex-wb-deco-text">
                  {isVendorViewer ? (
                    <>
                      Ideas &amp; peers
                      <br />
                      in one place
                    </>
                  ) : (
                    <>
                      Your celebration
                      <br />
                      starts here
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showCategorySelection && selectedCategory === 'all' && (
          <section className="cex-categories" id="chaavi-categories">
            <div className="cex-section-header">
              <span className="cex-section-title-icon">✨</span>
              <h2 className="cex-section-title">
                {isVendorViewer ? (
                  <>
                    Browse the <em>marketplace</em>
                  </>
                ) : (
                  <>
                    Plan Your <em>Perfect Event</em>
                  </>
                )}
              </h2>
            </div>
            <p className="cex-section-sub">
              {isVendorViewer
                ? 'Same categories and listings your clients use—tap in to explore.'
                : 'What are you looking for today?'}
            </p>
            <div className="cex-gold-rule" />
            <div className="cex-cat-grid">
              <button
                type="button"
                className="cex-cat-card cex-cat-themes"
                onClick={() => handleChaaviCategory('tent')}
              >
                <div className="cex-cat-card-icon">🎨</div>
                <div className="cex-cat-card-name">Themes</div>
                <div className="cex-cat-card-count">
                  {chaaviCounts.themeOptions > 0 ? `${chaaviCounts.themeOptions}+ options` : 'Browse themes'}
                </div>
                <div className="cex-cat-card-overlay" />
                <div className="cex-cat-card-corner">Explore →</div>
              </button>
              <button
                type="button"
                className="cex-cat-card cex-cat-food"
                onClick={() => handleChaaviCategory('caters')}
              >
                <div className="cex-cat-card-icon">🍽️</div>
                <div className="cex-cat-card-name">Catering</div>
                <div className="cex-cat-card-count">
                  {chaaviCounts.cateringVendors > 0
                    ? `${chaaviCounts.cateringVendors}+ vendors`
                    : 'Find caterers'}
                </div>
                <div className="cex-cat-card-overlay" />
                <div className="cex-cat-card-corner">Explore →</div>
              </button>
              <button
                type="button"
                className="cex-cat-card cex-cat-hotels"
                onClick={() => handleChaaviCategory('hotel')}
              >
                <div className="cex-cat-card-icon">🏨</div>
                <div className="cex-cat-card-name">Venues</div>
                <div className="cex-cat-card-count">
                  {chaaviCounts.venues > 0 ? `${chaaviCounts.venues}+ venues` : 'Find venues'}
                </div>
                <div className="cex-cat-card-overlay" />
                <div className="cex-cat-card-corner">Explore →</div>
              </button>
              <button
                type="button"
                className="cex-cat-card cex-cat-decor"
                onClick={() => handleChaaviCategory('tent')}
              >
                <div className="cex-cat-card-icon">📦</div>
                <div className="cex-cat-card-name">Décor</div>
                <div className="cex-cat-card-count">
                  {chaaviCounts.decorItems > 0 ? `${chaaviCounts.decorItems}+ items` : 'Décor & more'}
                </div>
                <div className="cex-cat-card-overlay" />
                <div className="cex-cat-card-corner">Explore →</div>
              </button>
            </div>
          </section>
        )}

      {/* Active Filters Chips */}
      <ActiveFiltersChips
        filters={appliedFilters}
        onRemoveFilter={(key) => {
          const newFilters = { ...appliedFilters, [key]: getDefaultValue(key) };
          setAppliedFilters(newFilters);
          applyFilters(newFilters);
        }}
        onClearAll={() => {
          const defaultFilters: FilterOptions = {
            eventType: 'all',
            category: 'all',
            location: 'all',
            radius: 10,
            budget: 'all',
            minBudget: 0,
            maxBudget: 100000,
            sortBy: 'default',
            minRating: 0,
          };
          setAppliedFilters(defaultFilters);
          applyFilters(defaultFilters);
        }}
      />

      {/* Business-Based Organization */}
      <Box sx={{ mt: 4 }}>
        {selectedCategory === 'all' ? (
          <>
            {/* Back to Categories Button - Show when category selection is hidden */}
            {!showCategorySelection ? (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <button type="button" className="cex-back-btn" onClick={() => setShowCategorySelection(true)}>
                  ← View categories
                </button>
              </Box>
            ) : null}
            {filteredBusinesses.length > 0 ? (
              <div className="cex-popular-intro">
                <p className="cex-section-sub">
                  {isVendorViewer
                    ? '📊 Marketplace snapshot · Vendors and services clients see in your region'
                    : '🔥 Popular Near You · Discover the best event services in your area'}
                </p>
              </div>
            ) : null}
            {/* All Businesses Section */}
            {sortBusinessesByDistance(filteredBusinesses).map((business, businessIndex) => {
            // Determine business type based on category
            const categoryLower = business.businessCategory?.toLowerCase() || '';
            const isCateringBusiness = categoryLower.includes('catering') ||
                                      categoryLower.includes('caters') ||
                                      categoryLower.includes('food') ||
                                      categoryLower.includes('restaurant') ||
                                      categoryLower.includes('cafe');
            const isTentBusiness = categoryLower.includes('tent') ||
                                  categoryLower.includes('decoration') ||
                                  categoryLower.includes('event') ||
                                  categoryLower.includes('wedding') ||
                                  categoryLower.includes('party');
            const isFarmhouseBusiness = categoryLower.includes('farmhouse') ||
                                        categoryLower.includes('farm house');
            
            // For catering businesses, only show plates and dishes
            // For tent businesses, only show themes and inventory
            // For others or mixed, show all
            const businessThemesList = (isCateringBusiness) ? [] : filterAndSortThemes(
              sortItemsByDistance(
                businessThemes[business.businessId] || []
              )
            );
            const businessInventoryList = (isCateringBusiness || isFarmhouseBusiness) ? [] : filterAndSortInventory(
              sortItemsByDistance(
                businessInventory[business.businessId] || []
              )
            );
            const businessPlatesList = (isTentBusiness && !isCateringBusiness) ? [] : filterAndSortPlates(
              sortItemsByDistance(
                businessPlates[business.businessId] || []
              )
            );
            const originalBusinessDishes = (isTentBusiness && !isCateringBusiness) ? [] : (
              businessDishes[business.businessId] || []
            );
            const businessDishesList = filterAndSortDishes(
              sortItemsByDistance(originalBusinessDishes)
            );
            
            // Skip businesses with no content (check original lists, not filtered)
            const hasOriginalDishes = originalBusinessDishes.length > 0;
            if (businessThemesList.length === 0 && businessInventoryList.length === 0 && businessPlatesList.length === 0 && !hasOriginalDishes) return null;
            
            const distance = businessDistances[business.businessId];
            
            return (
              <Box
                key={business.businessId}
                id={`vendor-${business.businessId}`}
                component="section"
                className="cex-vendor-section"
                sx={{ mb: 8 }}
              >
                  <Box sx={{ mb: 3 }}>
                    <div className="cex-vendor-header">
                      <div className="cex-vendor-name-row">
                        <h2 className="cex-vendor-name">{business.businessName}</h2>
                        {vendorThemesTopRated(businessThemesList) ? (
                          <span className="cex-vendor-badge">⭐ Top Rated</span>
                        ) : null}
                      </div>
                      <Tooltip title="Start a conversation with this vendor">
                        <button
                          type="button"
                          className="cex-chat-icon-btn"
                          onClick={() => handleStartChat(business)}
                          disabled={!!chatLoading[business.businessId]}
                          aria-label="Chat with vendor"
                        >
                          {chatLoading[business.businessId] ? '…' : '💬'}
                        </button>
                      </Tooltip>
                    </div>
                    <div className="cex-gold-rule" />
                    {distance !== undefined && userLocation ? (
                      <Box sx={{ display: 'flex', mb: 2 }}>
                        <Chip
                          icon={<LocationOnIcon sx={{ color: '#d4a847 !important' }} />}
                          label={formatDistance(distance)}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'rgba(212,168,71,0.35)',
                            color: '#e8c97a',
                            bgcolor: 'rgba(212,168,71,0.08)',
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>

                  {businessThemesList.length > 0 && !isCateringBusiness ? (
                    <Box sx={{ mb: 4 }}>
                      <div className="cex-themes-label">
                        🎭 Themes
                        <span className="cex-themes-meta">
                          &nbsp;· {businessThemesList.length} available
                        </span>
                      </div>
                      <div className="cex-themes-grid">
                        {businessThemesList.map((theme) => (
                          <div key={theme.themeId} id={`theme-${theme.themeId}`} className="cex-theme-card-wrap">
                            <ThemeCard
                              theme={theme}
                              business={business}
                              onBuyNow={handleThemeBuyNow}
                              showCartButton={true}
                              showBuyNowButton={true}
                              showActions={false}
                            />
                          </div>
                        ))}
                      </div>
                    </Box>
                  ) : null}

                  {/* Show inventory if available (only for non-catering businesses) */}
                  {businessInventoryList.length > 0 && !isCateringBusiness && (
                    <Box sx={{ mb: 4 }}>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                          mb: 3,
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          fontWeight: 'bold',
                          fontSize: { xs: 'clamp(1.25rem, 3vw, 1.5rem)', md: '1.75rem' },
                        }}
                      >
                        📦 Inventory ({businessInventoryList.length} available)
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: 2,
                          width: '100%',
                        }}
                      >
                        {businessInventoryList.map((item) => (
                          <Box
                            key={item.inventoryId}
                            id={`inventory-${item.inventoryId}`}
                            sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                          >
                            <InventoryCard
                              inventory={item}
                              business={business}
                              onBuyNow={handleInventoryBuyNow}
                              showCartButton={true}
                              showBuyNowButton={true}
                              showActions={false}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Show plates if available (only for catering businesses or when showing all) */}
                  {businessPlatesList.length > 0 && (!isTentBusiness || isCateringBusiness) && (
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 3, rowGap: 1.5 }}>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontWeight: 'bold',
                            fontSize: { xs: 'clamp(1.25rem, 3vw, 1.5rem)', md: '1.75rem' },
                          }}
                        >
                          🍽️ Plates ({businessPlatesList.length} available)
                        </Typography>
                        <ToggleButtonGroup
                          value={plateTypeFilter}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) {
                              setPlateTypeFilter(newValue);
                            }
                          }}
                          size="small"
                          sx={{
                            '& .MuiToggleButton-root': {
                              px: 2,
                              py: 0.5,
                              fontSize: '0.875rem',
                              '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                },
                              },
                            },
                          }}
                        >
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="veg">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#4caf50',
                                }}
                              />
                              Veg
                            </Box>
                          </ToggleButton>
                          <ToggleButton value="non-veg">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#f44336',
                                }}
                              />
                              Non-Veg
                            </Box>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      {plateTypeFilter !== 'all' && (
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`Filter: ${plateTypeFilter === 'veg' ? 'Vegetarian' : 'Non-Vegetarian'}`}
                            onDelete={() => setPlateTypeFilter('all')}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      )}
                      {businessPlatesList.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          {businessPlatesList.map((plate) => (
                            <Box
                              key={plate.plateId}
                              id={`plate-${plate.plateId}`}
                              sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                            >
                              <PlateCard
                                plate={plate}
                                business={business}
                                onEdit={() => {}} 
                                onDelete={() => {}} 
                                onUpdate={() => {}} 
                                onBuyNow={handlePlateBuyNow}
                                showCartButton={true}
                                showBuyNowButton={true}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 4,
                          px: 2,
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="body1" color="text.secondary">
                            No plates found matching the selected filter.
                            {plateTypeFilter !== 'all' && (
                              <Button 
                                size="small" 
                                onClick={() => setPlateTypeFilter('all')}
                                sx={{ ml: 1 }}
                              >
                                Show All
                              </Button>
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Show dishes section if available (only for catering businesses or when showing all) */}
                  {hasOriginalDishes && (!isTentBusiness || isCateringBusiness) && (
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 3, rowGap: 1.5 }}>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            color: '#E23744',
                            fontWeight: 'bold',
                            fontSize: { xs: 'clamp(1.25rem, 3vw, 1.5rem)', md: '1.75rem' },
                          }}
                        >
                          🍲 Dishes ({businessDishesList.length} available)
                        </Typography>
                        <ToggleButtonGroup
                          value={dishTypeFilter}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue !== null) {
                              setDishTypeFilter(newValue);
                            }
                          }}
                          size="small"
                          sx={{
                            '& .MuiToggleButton-root': {
                              px: 2,
                              py: 0.5,
                              fontSize: '0.875rem',
                              '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                },
                              },
                            },
                          }}
                        >
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="veg">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#4caf50',
                                }}
                              />
                              Veg
                            </Box>
                          </ToggleButton>
                          <ToggleButton value="non-veg">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#f44336',
                                }}
                              />
                              Non-Veg
                            </Box>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      {businessDishesList.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          {businessDishesList.map((dish) => (
                            <Box
                              key={dish.dishId}
                              id={`dish-${dish.dishId}`}
                              sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                            >
                              <DishCard
                                dish={dish}
                                business={business}
                                onEdit={() => {}} 
                                onDelete={() => {}} 
                                onUpdate={() => {}} 
                                onBuyNow={handleDishBuyNow}
                                showCartButton={true}
                                showBuyNowButton={true}
                                hideImageIndicator={true}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 4,
                          px: 2,
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="body1" color="text.secondary">
                            No dishes found matching the selected filter.
                            {dishTypeFilter !== 'all' && (
                              <Button 
                                size="small" 
                                onClick={() => setDishTypeFilter('all')}
                                sx={{ ml: 1 }}
                              >
                                Show All
                              </Button>
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
              </Box>
            );
          })}
          {filteredBusinesses.length > 0 ? (
            <section className="cex-popular-section">
              <div className="cex-section-header">
                <span className="cex-section-title-icon">🔥</span>
                <h2 className="cex-section-title">
                  {isVendorViewer ? (
                    <>
                      Spotlight <em>listings</em>
                    </>
                  ) : (
                    <>
                      More <em>Near You</em>
                    </>
                  )}
                </h2>
              </div>
              <div className="cex-gold-rule" />
              <div className="cex-popular-grid">
                {sortBusinessesByDistance(filteredBusinesses)
                  .slice(0, 6)
                  .map((b) => (
                    <button
                      type="button"
                      key={b.businessId}
                      className="cex-vendor-card"
                      onClick={() =>
                        document
                          .getElementById(`vendor-${b.businessId}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                    >
                      <div className="cex-vc-img">
                        {vendorCardEmoji(b)}
                        <div className="cex-vc-img-overlay" />
                      </div>
                      <div className="cex-vc-body">
                        <div className="cex-vc-name">{b.businessName}</div>
                        <div className="cex-vc-meta">
                          {b.businessAddress || 'Near you'}
                          {businessDistances[b.businessId] !== undefined && userLocation
                            ? ` · ${formatDistance(businessDistances[b.businessId])} away`
                            : ''}
                        </div>
                        <div className="cex-vc-tags">
                          <span className="cex-vc-tag">📍 {b.businessCategory || 'Vendor'}</span>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </section>
          ) : null}
          </>
        ) : (selectedCategory === 'catering' || selectedCategory === 'caters') ? (
          /* Catering & Food Section */
          <Box>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setSelectedCategory('all');
                  setShowCategorySelection(true);
                }}
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                Back to All Services
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontSize: { xs: 'clamp(1.5rem, 4vw, 2rem)', md: '2.5rem' },
                  }}
                >
                  🍽️ Catering Services
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: 'clamp(0.9rem, 2vw, 1.1rem)', md: '1.1rem' },
                  }}
                >
                  Find the perfect food and catering for your event
                </Typography>
              </Box>
            </Box>
            <Typography variant="h4" gutterBottom sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              borderBottom: '3px solid',
              borderColor: 'primary.main',
              pb: 2,
              mb: 4
            }}>
              🍽️ Catering & Food
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setBuildPlateOpen(true);
                  setComparisonResults([]);
                  setCustomBuildDishes([]);
                  setCustomBuildDishName('');
                  setCustomBuildDishType('veg');
                  setCustomBuildDishQuantity('1');
                  setCustomBuildDishError(null);
                }}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  boxShadow: '0 6px 18px rgba(240, 147, 251, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                    boxShadow: '0 8px 22px rgba(240, 147, 251, 0.6)',
                  },
                }}
              >
                Build Your Plate
              </Button>
            </Box>
            
            
            {sortBusinessesByDistance(filteredBusinesses).map((business, businessIndex) => {
              const businessPlatesList = filterAndSortPlates(
                sortItemsByDistance(
                  businessPlates[business.businessId] || []
                )
              );
              const originalBusinessDishes = businessDishes[business.businessId] || [];
              const businessDishesList = filterAndSortDishes(
                sortItemsByDistance(originalBusinessDishes)
              );
              const hasOriginalDishes = originalBusinessDishes.length > 0;
              // Skip businesses with no content (check original lists, not filtered)
              if (businessPlatesList.length === 0 && !hasOriginalDishes) return null;
              
              const distance = businessDistances[business.businessId];
              
              return (
                <Box key={business.businessId} sx={{ mb: 6 }}>
                    {/* Business Name as Heading */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2, position: 'relative' }}>
                        <Typography variant="h3" gutterBottom sx={{ 
                          fontWeight: 'bold', 
                          color: 'primary.main',
                          borderBottom: '3px solid',
                          borderColor: 'primary.main',
                          pb: 1,
                          display: 'inline-block',
                          textAlign: 'center',
                        }}>
                          {business.businessName}
                        </Typography>
                        <Tooltip title="Start a conversation with this vendor">
                          <IconButton
                            color="primary"
                            onClick={() => handleStartChat(business)}
                            disabled={chatLoading[business.businessId]}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              bgcolor: 'primary.main',
                              color: 'white',
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              boxShadow: 2,
                              '&:hover': {
                                bgcolor: 'primary.dark',
                                boxShadow: 4,
                                transform: 'translateY(-2px) scale(1.1)',
                                '& .MuiSvgIcon-root': {
                                  transform: 'scale(1.2) rotate(5deg)',
                                },
                              },
                              '&:active': {
                                transform: 'translateY(0) scale(0.95)',
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'action.disabledBackground',
                                color: 'action.disabled',
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          >
                            <ChatIcon 
                              sx={{ 
                                transition: 'all 0.3s ease-in-out',
                                animation: chatLoading[business.businessId] ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                '@keyframes pulse': {
                                  '0%, 100%': {
                                    transform: 'scale(1)',
                                  },
                                  '50%': {
                                    transform: 'scale(1.1)',
                                  },
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        {distance !== undefined && userLocation && (
                          <Chip
                            icon={<LocationOnIcon />}
                            label={formatDistance(distance)}
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Plates List */}
                    {businessPlatesList.length > 0 ? (
                      <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 3, rowGap: 1.5 }}>
                          <Typography variant="h5" sx={{ color: 'primary.main' }}>
                            🍽️ Plates ({businessPlatesList.length} available)
                          </Typography>
                          <ToggleButtonGroup
                            value={plateTypeFilter}
                            exclusive
                            onChange={(_, newValue) => {
                              if (newValue !== null) {
                                setPlateTypeFilter(newValue);
                              }
                            }}
                            size="small"
                            sx={{
                              '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                fontSize: '0.875rem',
                                '&.Mui-selected': {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'primary.dark',
                                  },
                                },
                              },
                            }}
                          >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="veg">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#4caf50',
                                  }}
                                />
                                Veg
                              </Box>
                            </ToggleButton>
                            <ToggleButton value="non-veg">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#f44336',
                                  }}
                                />
                                Non-Veg
                              </Box>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        {plateTypeFilter !== 'all' && (
                          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={`Filter: ${plateTypeFilter === 'veg' ? 'Vegetarian' : 'Non-Vegetarian'}`}
                              onDelete={() => setPlateTypeFilter('all')}
                              color="primary"
                              size="small"
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          {businessPlatesList.map((plate) => (
                          <Box
                            key={plate.plateId}
                            id={`plate-${plate.plateId}`}
                            sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                          >
                            <PlateCard
                              plate={plate}
                            business={business}
                            onEdit={() => {}} 
                            onDelete={() => {}} 
                            onUpdate={() => {}} 
                            onBuyNow={handlePlateBuyNow}
                            showCartButton={true}
                            showBuyNowButton={true}
                          />
                        </Box>
                      ))}
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        px: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="body1" color="text.secondary">
                          No plates found matching the selected filter.
                          {plateTypeFilter !== 'all' && (
                            <Button 
                              size="small" 
                              onClick={() => setPlateTypeFilter('all')}
                              sx={{ ml: 1 }}
                            >
                              Show All
                            </Button>
                          )}
                        </Typography>
                      </Box>
                    )}

                    {/* Dishes List */}
                    {hasOriginalDishes && (
                      <Box sx={{ mt: 4 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start', gap: 2, mb: 3, rowGap: 1.5 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              color: '#E23744',
                              fontWeight: 'bold',
                              fontSize: { xs: 'clamp(1.25rem, 3vw, 1.5rem)', md: '1.75rem' },
                            }}
                          >
                            🍲 Dishes ({businessDishesList.length} available)
                          </Typography>
                          <ToggleButtonGroup
                            value={dishTypeFilter}
                            exclusive
                            onChange={(_, newValue) => {
                              if (newValue !== null) {
                                setDishTypeFilter(newValue);
                              }
                            }}
                            size="small"
                            sx={{
                              '& .MuiToggleButton-root': {
                                px: 2,
                                py: 0.5,
                                fontSize: '0.875rem',
                                '&.Mui-selected': {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'primary.dark',
                                  },
                                },
                              },
                            }}
                          >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="veg">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#4caf50',
                                  }}
                                />
                                Veg
                              </Box>
                            </ToggleButton>
                            <ToggleButton value="non-veg">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: '#f44336',
                                  }}
                                />
                                Non-Veg
                              </Box>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        {businessDishesList.length > 0 ? (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                              gap: 2,
                              width: '100%',
                            }}
                          >
                            {businessDishesList.map((dish) => (
                              <Box
                                key={dish.dishId}
                                id={`dish-${dish.dishId}`}
                                sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                              >
                                <DishCard
                                  dish={dish}
                                  business={business}
                                  onEdit={() => {}} 
                                  onDelete={() => {}} 
                                  onUpdate={() => {}} 
                                  onBuyNow={handleDishBuyNow}
                                  showCartButton={true}
                                  showBuyNowButton={true}
                                  hideImageIndicator={true}
                                />
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            textAlign: 'center', 
                            py: 4,
                            px: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider'
                          }}>
                            <Typography variant="body1" color="text.secondary">
                              No dishes found matching the selected filter.
                              {dishTypeFilter !== 'all' && (
                                <Button 
                                  size="small" 
                                  onClick={() => setDishTypeFilter('all')}
                                  sx={{ ml: 1 }}
                                >
                                  Show All
                                </Button>
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                </Box>
              );
            })}
            
            
            {/* No data message */}
            {filteredBusinesses.length === 0 && plates.length === 0 && dishes.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No catering businesses found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try selecting a different category
                </Typography>
              </Box>
            )}
          </Box>
        ) : selectedCategory === 'hotel' ? (
          /* Hotels & Farm Houses Section */
          <Box>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setSelectedCategory('all');
                  setShowCategorySelection(true);
                }}
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                Back to All Services
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontSize: { xs: 'clamp(1.5rem, 4vw, 2rem)', md: '2.5rem' },
                  }}
                >
                  🏨 Hotels & Farm Houses
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: 'clamp(0.9rem, 2vw, 1.1rem)', md: '1.1rem' },
                  }}
                >
                  Discover beautiful venues for your events and celebrations
                </Typography>
              </Box>
            </Box>

            {/* Hotels and Farmhouses Grid */}
            {filteredBusinesses.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No hotels or farmhouses found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try selecting a different category
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedCategory('all');
                    setShowCategorySelection(true);
                  }}
                  sx={{ mt: 2 }}
                >
                  View Category Selection
                </Button>
              </Box>
            ) : (
              <Box>
                {sortBusinessesByDistance(filteredBusinesses).map((business) => {
                  const distance = businessDistances[business.businessId];
                  const businessThemesList = filterAndSortThemes(
                    sortItemsByDistance(
                      businessThemes[business.businessId] || []
                    )
                  );

                  if (businessThemesList.length === 0) return null;

                  return (
                    <Box key={business.businessId} sx={{ mb: 8 }}>
                      <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap', position: 'relative' }}>
                          <Typography
                            variant="h2"
                            gutterBottom
                            sx={{
                              fontWeight: 'bold',
                              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                              borderBottom: '4px solid',
                              borderImage: 'linear-gradient(135deg, #fa709a, #fee140) 1',
                              pb: 2,
                              display: 'inline-block',
                              textAlign: 'center',
                              fontSize: { xs: 'clamp(1.5rem, 4vw, 2rem)', md: '2.5rem' },
                              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          >
                            {business.businessName}
                          </Typography>
                          <Tooltip title="Start a conversation with this vendor">
                            <IconButton
                              color="primary"
                              onClick={() => handleStartChat(business)}
                              disabled={chatLoading[business.businessId]}
                              sx={{
                                position: 'absolute',
                                right: 0,
                                bgcolor: 'primary.main',
                                color: 'white',
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                boxShadow: 2,
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                  boxShadow: 4,
                                  transform: 'translateY(-2px) scale(1.1)',
                                  '& .MuiSvgIcon-root': {
                                    transform: 'scale(1.2) rotate(5deg)',
                                  },
                                },
                                '&:active': {
                                  transform: 'translateY(0) scale(0.95)',
                                },
                                '&.Mui-disabled': {
                                  bgcolor: 'action.disabledBackground',
                                  color: 'action.disabled',
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              }}
                            >
                              <ChatIcon
                                sx={{
                                  transition: 'all 0.3s ease-in-out',
                                  animation: chatLoading[business.businessId] ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                  '@keyframes pulse': {
                                    '0%, 100%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.1)' },
                                  },
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                          {distance !== undefined && userLocation && (
                            <Chip
                              icon={<LocationOnIcon />}
                              label={formatDistance(distance)}
                              color="primary"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                          🏨 Themes from {business.businessName}
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          {businessThemesList.map((theme) => (
                            <Box
                              key={theme.themeId}
                              id={`theme-${theme.themeId}`}
                              sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                            >
                              <ThemeCard
                                theme={theme}
                                business={business}
                                onBuyNow={handleThemeBuyNow}
                                showCartButton={true}
                                showBuyNowButton={true}
                                showActions={false}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        ) : (
          /* Tent & Events Section */
          <Box>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setSelectedCategory('all');
                  setShowCategorySelection(true);
                }}
                sx={{
                  mb: 2,
                  color: 'primary.main',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                Back to All Services
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontSize: { xs: 'clamp(1.5rem, 4vw, 2rem)', md: '2.5rem' },
                  }}
                >
                  🎨 Decoration & Themes
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: 'clamp(0.9rem, 2vw, 1.1rem)', md: '1.1rem' },
                  }}
                >
                  Discover beautiful themes and decorations for your celebration
                </Typography>
              </Box>
            </Box>
            {sortBusinessesByDistance(filteredBusinesses).map((business, businessIndex) => {
            const businessThemesList = filterAndSortThemes(
              sortItemsByDistance(
                businessThemes[business.businessId] || []
              )
            );
            const businessInventoryList = filterAndSortInventory(
              sortItemsByDistance(
                businessInventory[business.businessId] || []
              )
            );
            
            // Skip businesses with no themes or inventory
            if (businessThemesList.length === 0 && businessInventoryList.length === 0) return null;
            
            const distance = businessDistances[business.businessId];
            
            return (
              <Box key={business.businessId} sx={{ mb: 8 }}>
                  {/* Business Name as Main Heading */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap', position: 'relative' }}>
                      <Typography 
                        variant="h2" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          borderBottom: '4px solid',
                          borderImage: 'linear-gradient(135deg, #667eea, #764ba2, #f093fb) 1',
                          pb: 2,
                          display: 'inline-block',
                          textAlign: 'center',
                          fontSize: { xs: 'clamp(1.5rem, 4vw, 2rem)', md: '2.5rem' },
                          textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        {business.businessName}
                      </Typography>
                      <Tooltip title="Start a conversation with this vendor">
                        <IconButton
                          color="primary"
                          onClick={() => handleStartChat(business)}
                          disabled={chatLoading[business.businessId]}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            bgcolor: 'primary.main',
                            color: 'white',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            boxShadow: 2,
                            '&:hover': {
                              bgcolor: 'primary.dark',
                              boxShadow: 4,
                              transform: 'translateY(-2px) scale(1.1)',
                              '& .MuiSvgIcon-root': {
                                transform: 'scale(1.2) rotate(5deg)',
                              },
                            },
                            '&:active': {
                              transform: 'translateY(0) scale(0.95)',
                            },
                            '&.Mui-disabled': {
                              bgcolor: 'action.disabledBackground',
                              color: 'action.disabled',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          <ChatIcon 
                            sx={{ 
                              transition: 'all 0.3s ease-in-out',
                              animation: chatLoading[business.businessId] ? 'pulse 1.5s ease-in-out infinite' : 'none',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  transform: 'scale(1)',
                                },
                                '50%': {
                                  transform: 'scale(1.1)',
                                },
                              },
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                      {distance !== undefined && userLocation && (
                        <Chip
                          icon={<LocationOnIcon />}
                          label={formatDistance(distance)}
                          color="primary"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Business Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label={`${business.businessName} tabs`}>
                      <Tab
                        icon={<PaletteIcon />}
                        label={`Themes (${businessThemesList.length})`}
                        iconPosition="start"
                      />
                      <Tab
                        icon={<InventoryIcon />}
                        label={`Inventory (${businessInventoryList.length})`}
                        iconPosition="start"
                      />
                    </Tabs>
                  </Box>

                  {/* Themes Tab for this business */}
                  {activeTab === 0 && businessThemesList.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        🎨 Themes from {business.businessName}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: 2,
                          width: '100%',
                        }}
                      >
                        {businessThemesList.map((theme) => (
                          <Box
                            key={theme.themeId}
                            id={`theme-${theme.themeId}`}
                            sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                          >
                            <ThemeCard
                              theme={theme}
                              business={business}
                              onBuyNow={handleThemeBuyNow}
                              showCartButton={true}
                              showBuyNowButton={true}
                              showActions={false}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Inventory Tab for this business */}
                  {activeTab === 1 && businessInventoryList.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                        📦 Inventory from {business.businessName}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: 2,
                          width: '100%',
                        }}
                      >
                        {businessInventoryList.map((item) => (
                          <Box
                            key={item.inventoryId}
                            id={`inventory-${item.inventoryId}`}
                            sx={{ minWidth: 0, width: '100%', scrollMarginTop: '100px' }}
                          >
                            <InventoryCard
                              inventory={item}
                              business={business}
                              onBuyNow={handleInventoryBuyNow}
                              showCartButton={true}
                              showBuyNowButton={true}
                              showActions={false}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}


                  {/* No content message */}
                  {activeTab === 0 && businessThemesList.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No themes available from {business.businessName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Check back later for new themes
                      </Typography>
                    </Box>
                  )}

                  {activeTab === 1 && businessInventoryList.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No inventory available from {business.businessName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Check back later for new items
                      </Typography>
                    </Box>
                  )}

              </Box>
            );
          })}
          </Box>
        )}
      </Box>

      <Dialog
        open={buildPlateOpen}
        onClose={() => {
          setBuildPlateOpen(false);
          setCustomBuildDishError(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Build Your Plate</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Plate Name"
              value={buildPlateName}
              onChange={(e) => setBuildPlateName(e.target.value)}
              placeholder="e.g., Birthday Feast"
              sx={{ mb: 2 }}
            />
            <Autocomplete<DishOption, true, false, false>
              multiple
              options={dishOptions}
              value={selectedDishKeys
                .map(key => dishOptionMap.get(key))
                .filter((option): option is DishOption => Boolean(option))}
              getOptionLabel={(option) => option?.label || ''}
              onChange={(_, newValue) => {
                const nextKeys = newValue.map(option => option.key);
                setSelectedDishKeys(nextKeys);
                setSelectedDishQuantities(prev => {
                  const next = { ...prev };
                  nextKeys.forEach(key => {
                    if (!next[key]) {
                      next[key] = 1;
                    }
                  });
                  Object.keys(next).forEach(key => {
                    if (!nextKeys.includes(key)) {
                      delete next[key];
                    }
                  });
                  return next;
                });
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.key}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body1">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Available from {option.vendorsCount} vendor{option.vendorsCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Dishes"
                  placeholder="Search dish names"
                />
              )}
            />

            {selectedDishKeys.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Selected Dishes
                </Typography>
                {selectedDishKeys.map(key => {
                  const option = dishOptionMap.get(key);
                  if (!option) return null;
                  return (
                    <Paper key={key} sx={{ p: 2, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Available from {option.vendorsCount} vendor{option.vendorsCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <TextField
                          label="Quantity"
                          type="number"
                          size="small"
                          inputProps={{ min: 1 }}
                          value={selectedDishQuantities[key] || 1}
                          onChange={(e) => {
                            const nextValue = Math.max(1, Number(e.target.value || 1));
                            setSelectedDishQuantities(prev => ({ ...prev, [key]: nextValue }));
                          }}
                          sx={{ width: 120 }}
                        />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Add a Custom Dish
            </Typography>
            {customBuildDishError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {customBuildDishError}
              </Alert>
            )}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Dish Name"
                  value={customBuildDishName}
                  onChange={(e) => setCustomBuildDishName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={customBuildDishType}
                    onChange={(e) => setCustomBuildDishType(e.target.value as 'veg' | 'non-veg')}
                  >
                    <MenuItem value="veg">Veg</MenuItem>
                    <MenuItem value="non-veg">Non-Veg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Qty"
                  type="number"
                  value={customBuildDishQuantity}
                  onChange={(e) => setCustomBuildDishQuantity(e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button variant="outlined" fullWidth onClick={handleAddCustomBuildDish}>
                  Add
                </Button>
              </Grid>
            </Grid>

            {customBuildDishes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Custom dishes added (not used for vendor matching):
                </Typography>
                {customBuildDishes.map((dish) => (
                  <Paper key={dish.id} sx={{ p: 2, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {dish.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Price to be provided by vendor
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={dish.dishType === 'veg' ? 'Veg' : 'Non-Veg'}
                            size="small"
                            color={dish.dishType === 'veg' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <TextField
                        label="Quantity"
                        type="number"
                        size="small"
                        inputProps={{ min: 1 }}
                        value={dish.quantity}
                        onChange={(e) => {
                          const nextValue = Math.max(1, Number(e.target.value || 1));
                          setCustomBuildDishes(prev =>
                            prev.map(item => item.id === dish.id ? { ...item, quantity: nextValue } : item)
                          );
                        }}
                        sx={{ width: 120 }}
                      />
                      <Button
                        color="error"
                        size="small"
                        onClick={() => setCustomBuildDishes(prev => prev.filter(item => item.id !== dish.id))}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {comparisonResults.length > 0 ? (
              <Box>
                <Tabs value={comparisonTab} onChange={(_, value) => setComparisonTab(value)}>
                  <Tab label="Best Match" />
                  <Tab label="Cheapest" />
                  <Tab label="Top Rated" />
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  {(comparisonTab === 0 ? comparisonBuckets.bestMatch :
                    comparisonTab === 1 ? comparisonBuckets.cheapest :
                    comparisonBuckets.topRated
                  ).map(result => {
                    const business = businessMap.get(result.businessId);
                    return (
                      <Paper key={result.businessId} sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {result.businessName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {business?.businessPhone || business?.phoneNumber || 'Phone not available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Matched dishes: {result.matchedDishes}
                            </Typography>
                            {result.matchedDishNames.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {result.matchedDishNames.join(', ')}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Total Price: ₹{Math.round(result.totalPrice)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Avg Rating: {result.avgRating.toFixed(1)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                  {(comparisonTab === 0 ? comparisonBuckets.bestMatch :
                    comparisonTab === 1 ? comparisonBuckets.cheapest :
                    comparisonBuckets.topRated
                  ).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No vendors match your selected dishes.
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select dishes and click Compare to see matching vendors.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuildPlateOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleBuildPlateCompare}
            disabled={selectedDishKeys.length === 0}
          >
            Compare Vendors
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        onApply={(filters) => {
          setAppliedFilters(filters);
          applyFilters(filters);
        }}
        onReset={() => {
          const defaultFilters: FilterOptions = {
            eventType: 'all',
            category: 'all',
            location: 'all',
            radius: 10,
            budget: 'all',
            minBudget: 0,
            maxBudget: 100000,
            sortBy: 'default',
            minRating: 0,
          };
          setAppliedFilters(defaultFilters);
          applyFilters(defaultFilters);
        }}
        initialFilters={appliedFilters}
      />

      <button
        type="button"
        className="cex-fab"
        title={isVendorViewer ? 'Business messages' : 'Chat with us'}
        aria-label={isVendorViewer ? 'Open vendor chat' : 'Open chat'}
        onClick={() => navigate(isVendorViewer ? '/vendor-chat' : '/client-chat')}
      >
        💬
      </button>
    </main>
    </div>
  );
};

export default ClientExplore;
